import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, ChevronDown } from 'lucide-react'

interface AutocompleteInputProps {
  value: string
  onChange: (value: string) => void
  onSuggestionsFetch: (query: string) => Promise<{ suggestions: string[] }>
  placeholder?: string
  debounceMs?: number
  minQueryLength?: number
  className?: string
  disabled?: boolean
  id?: string
}

export function AutocompleteInput({
  value,
  onChange,
  onSuggestionsFetch,
  placeholder = 'Szukaj...',
  debounceMs = 300,
  minQueryLength = 2,
  className = '',
  disabled = false,
  ...props
}: AutocompleteInputProps) {
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Debounced search function
  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      if (query.length < minQueryLength) {
        setSuggestions([])
        setIsOpen(false)
        return
      }

      debounceRef.current = setTimeout(async () => {
        setIsLoading(true)
        try {
          const result = await onSuggestionsFetch(query)
          setSuggestions(result.suggestions || [])
          setIsOpen(true)
          setHighlightedIndex(-1)
        } catch (error) {
          console.error('Error fetching suggestions:', error)
          setSuggestions([])
          setIsOpen(false)
        } finally {
          setIsLoading(false)
        }
      }, debounceMs)
    },
    [onSuggestionsFetch, debounceMs, minQueryLength],
  )

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    debouncedSearch(newValue)
  }

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: string) => {
    onChange(suggestion)
    setIsOpen(false)
    setSuggestions([])
    inputRef.current?.focus()
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleSuggestionSelect(suggestions[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  // Handle clear button
  const handleClear = () => {
    onChange('')
    setSuggestions([])
    setIsOpen(false)
    inputRef.current?.focus()
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        listRef.current &&
        !listRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex]
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        })
      }
    }
  }, [highlightedIndex])

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => value && suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full px-3 py-2 pl-10 pr-10 border border-border rounded-md
            focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand
            disabled:bg-surface-inset disabled:cursor-not-allowed
            ${isOpen ? 'rounded-b-none border-b-0' : ''}
          `}
          {...props}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-faint w-4 h-4" />

        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-8 top-1/2 transform -translate-y-1/2 text-ink-faint hover:text-ink-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand"></div>
          </div>
        )}

        {!isLoading && suggestions.length > 0 && (
          <ChevronDown
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-faint transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div
          ref={listRef}
          data-testid="autocomplete-suggestions"
          className="absolute z-50 w-full bg-surface-raised border border-border border-t-0 rounded-b-md max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => handleSuggestionSelect(suggestion)}
              className={`
                w-full px-3 py-2 text-left hover:bg-surface-hover focus:bg-surface-hover focus:outline-none
                ${index === highlightedIndex ? 'bg-brand-bg text-brand' : 'text-ink'}
              `}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {isOpen && suggestions.length === 0 && !isLoading && value.length >= minQueryLength && (
        <div className="absolute z-50 w-full bg-surface-raised border border-border border-t-0 rounded-b-md p-3 text-ink-muted text-sm">
          Brak wyników dla &apos;{value}&apos;
        </div>
      )}
    </div>
  )
}
